import React, { useEffect, useMemo, useState } from "react";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { getApiErrorMessage } from "../../store/api/baseApi";
import {
  useDeleteProjectTaskMutation,
  useFetchProjectsQuery,
  useSaveProjectTaskMutation,
} from "../../store/api/projectsApi";
import {
  ProjectRecord,
  ProjectTaskProgress,
  getProjectProgressSummary,
  getTaskDelayDays,
  getTaskHealth,
} from "./adminData";
import "./admin.css";

interface TaskFormState {
  id?: string;
  moduleName: string;
  owner: string;
  plannedPercent: string;
  actualPercent: string;
  plannedEndDate: string;
  actualEndDate: string;
  notes: string;
}

const emptyTaskForm: TaskFormState = {
  moduleName: "",
  owner: "",
  plannedPercent: "0",
  actualPercent: "0",
  plannedEndDate: "",
  actualEndDate: "",
  notes: "",
};

const toTaskFormState = (task?: ProjectTaskProgress): TaskFormState => ({
  id: task?.id,
  moduleName: task?.moduleName ?? "",
  owner: task?.owner ?? "",
  plannedPercent: String(task?.plannedPercent ?? 0),
  actualPercent: String(task?.actualPercent ?? 0),
  plannedEndDate: task?.plannedEndDate ?? "",
  actualEndDate: task?.actualEndDate ?? "",
  notes: task?.notes ?? "",
});

const healthBadgeClass: Record<string, string> = {
  completed: "text-bg-success",
  delayed: "text-bg-danger",
  "on-track": "text-bg-primary",
};

const ProjectProgressPage = () => {
  const { data: projects = [], isLoading, error } = useFetchProjectsQuery();
  const [saveProjectTask, { isLoading: isSavingTask }] = useSaveProjectTaskMutation();
  const [deleteProjectTask, { isLoading: isDeletingTask }] = useDeleteProjectTaskMutation();
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [validated, setValidated] = useState(false);
  const [taskForm, setTaskForm] = useState<TaskFormState>(emptyTaskForm);
  const [actionError, setActionError] = useState("");

  const selectedProject = useMemo(
    () => projects.find((project) => project.id === selectedProjectId) ?? projects[0] ?? null,
    [projects, selectedProjectId]
  );

  useEffect(() => {
    if (projects.length > 0 && !selectedProjectId) {
      setSelectedProjectId(projects[0].id);
    }
  }, [projects, selectedProjectId]);

  useEffect(() => {
    setTaskForm(emptyTaskForm);
    setValidated(false);
    setActionError("");
  }, [selectedProjectId]);

  const summary = selectedProject ? getProjectProgressSummary(selectedProject) : null;
  const errorMessage = error ? getApiErrorMessage(error as FetchBaseQueryError) : "";

  const handleEditTask = (task: ProjectTaskProgress) => {
    setTaskForm(toTaskFormState(task));
    setValidated(false);
    setActionError("");
  };

  const handleDeleteTask = async (project: ProjectRecord, task: ProjectTaskProgress) => {
    setActionError("");

    try {
      await deleteProjectTask({
        projectId: project.id,
        taskId: task.id,
        projectSnapshot: project,
      }).unwrap();

      if (taskForm.id === task.id) {
        resetForm();
      }
    } catch (apiError) {
      setActionError(getApiErrorMessage(apiError as FetchBaseQueryError));
    }
  };

  const resetForm = () => {
    setTaskForm(emptyTaskForm);
    setValidated(false);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!event.currentTarget.checkValidity() || !selectedProject) {
      event.stopPropagation();
      setValidated(true);
      return;
    }

    setActionError("");

    try {
      await saveProjectTask({
        projectId: selectedProject.id,
        projectSnapshot: selectedProject,
        task: {
          id: taskForm.id,
          moduleName: taskForm.moduleName.trim(),
          owner: taskForm.owner.trim(),
          plannedPercent: Number(taskForm.plannedPercent),
          actualPercent: Number(taskForm.actualPercent),
          plannedEndDate: taskForm.plannedEndDate,
          actualEndDate: taskForm.actualEndDate || undefined,
          notes: taskForm.notes.trim(),
        },
      }).unwrap();

      resetForm();
    } catch (apiError) {
      setActionError(getApiErrorMessage(apiError as FetchBaseQueryError));
    }
  };

  const busy = isSavingTask || isDeletingTask;

  return (
    <section className='admin-page'>
      <div className='container-fluid px-0'>
        {errorMessage ? <div className='alert alert-danger mb-4'>{errorMessage}</div> : null}
        {actionError ? <div className='alert alert-danger mb-4'>{actionError}</div> : null}

        <div className='row g-4'>
          <div className='col-xl-4'>
            <div style={{ background: "#fff", borderRadius: 14, border: "1.5px solid #e2e8f0", overflow: "hidden", boxShadow: "0 2px 12px #0000000a", height: "100%" }}>
              {/* Gradient header */}
              <div style={{ background: taskForm.id ? "linear-gradient(135deg, #78350f, #d97706, #f59e0b)" : "linear-gradient(135deg, #134e4a, #0d9488, #14b8a6)", padding: "10px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 30, height: 30, borderRadius: 8, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <i className={taskForm.id ? "bi bi-pencil-square" : "bi bi-graph-up-arrow"} style={{ color: "#fff", fontSize: 14 }} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: "0.9rem", color: "#fff", lineHeight: 1.2 }}>Task Progress Entry</div>
                    <div style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.75)" }}>Module-wise planned vs actual progress</div>
                  </div>
                </div>
                {taskForm.id && <span style={{ background: "rgba(255,255,255,0.22)", color: "#fff", border: "1px solid rgba(255,255,255,0.35)", borderRadius: 20, padding: "3px 12px", fontSize: "0.74rem", fontWeight: 600 }}>Editing</span>}
              </div>

              <div style={{ padding: "18px 20px 16px" }}>
                {/* Project selector */}
                <div style={{ borderLeft: "3px solid #6366f1", paddingLeft: 10, marginBottom: 16 }}>
                  <label htmlFor='progressProject' className='form-label mb-1 fw-semibold' style={{ fontSize: "0.84rem", color: "#334155" }}>Select Project</label>
                  <div className='input-group input-group-sm'>
                    <span className='input-group-text' style={{ background: "#eef2ff", borderColor: "#c7d2fe", color: "#6366f1" }}><i className='bi bi-folder-fill' style={{ fontSize: 12 }} /></span>
                    <select id='progressProject' className='form-select form-select-sm' style={{ borderColor: "#c7d2fe", boxShadow: "none" }} value={selectedProject?.id ?? ""} disabled={isLoading || projects.length === 0 || busy} onChange={(event) => setSelectedProjectId(event.target.value)}>
                      {projects.map((project) => <option key={project.id} value={project.id}>{project.title}</option>)}
                    </select>
                  </div>
                </div>

                <form noValidate className={validated ? "was-validated" : ""} onSubmit={handleSubmit}>
                  <div className='row g-3 align-items-end'>
                    <div className='col-12'>
                      <div style={{ borderLeft: "3px solid #0ea5e9", paddingLeft: 10 }}>
                        <label htmlFor='taskModule' className='form-label mb-1 fw-semibold' style={{ fontSize: "0.84rem", color: "#334155" }}>
                          Module Name
                          <small className='d-block fw-normal text-muted' style={{ fontSize: "0.71rem" }}>Name of the work package or construction module</small>
                        </label>
                        <div className='input-group input-group-sm'>
                          <span className='input-group-text' style={{ background: "#e0f2fe", borderColor: "#bae6fd", color: "#0ea5e9" }}><i className='bi bi-layers-fill' style={{ fontSize: 12 }} /></span>
                          <input id='taskModule' className='form-control form-control-sm' style={{ borderColor: "#bae6fd", boxShadow: "none" }} title='Module or work package name' value={taskForm.moduleName} onChange={(e) => setTaskForm((p) => ({ ...p, moduleName: e.target.value }))} minLength={2} required />
                        </div>
                        <div className='invalid-feedback'>Enter the module or work package name.</div>
                      </div>
                    </div>

                    <div className='col-12'>
                      <div style={{ borderLeft: "3px solid #8b5cf6", paddingLeft: 10 }}>
                        <label htmlFor='taskOwner' className='form-label mb-1 fw-semibold' style={{ fontSize: "0.84rem", color: "#334155" }}>
                          Responsible Team
                          <small className='d-block fw-normal text-muted' style={{ fontSize: "0.71rem" }}>Team or person responsible for this module</small>
                        </label>
                        <div className='input-group input-group-sm'>
                          <span className='input-group-text' style={{ background: "#ede9fe", borderColor: "#ddd6fe", color: "#7c3aed" }}><i className='bi bi-people-fill' style={{ fontSize: 12 }} /></span>
                          <input id='taskOwner' className='form-control form-control-sm' style={{ borderColor: "#ddd6fe", boxShadow: "none" }} title='Responsible team or owner' value={taskForm.owner} onChange={(e) => setTaskForm((p) => ({ ...p, owner: e.target.value }))} minLength={2} required />
                        </div>
                        <div className='invalid-feedback'>Enter the responsible team or owner.</div>
                      </div>
                    </div>

                    <div className='col-6'>
                      <div style={{ borderLeft: "3px solid #3b82f6", paddingLeft: 10 }}>
                        <label htmlFor='taskPlannedPercent' className='form-label mb-1 fw-semibold' style={{ fontSize: "0.84rem", color: "#334155" }}>Planned %</label>
                        <div className='input-group input-group-sm'>
                          <span className='input-group-text' style={{ background: "#dbeafe", borderColor: "#bfdbfe", color: "#1d4ed8" }}><i className='bi bi-bar-chart-fill' style={{ fontSize: 11 }} /></span>
                          <input id='taskPlannedPercent' type='number' className='form-control form-control-sm' style={{ borderColor: "#bfdbfe", boxShadow: "none" }} min={0} max={100} value={taskForm.plannedPercent} onChange={(e) => setTaskForm((p) => ({ ...p, plannedPercent: e.target.value }))} required />
                        </div>
                        <div className='invalid-feedback'>0–100</div>
                      </div>
                    </div>

                    <div className='col-6'>
                      <div style={{ borderLeft: "3px solid #10b981", paddingLeft: 10 }}>
                        <label htmlFor='taskActualPercent' className='form-label mb-1 fw-semibold' style={{ fontSize: "0.84rem", color: "#334155" }}>Actual %</label>
                        <div className='input-group input-group-sm'>
                          <span className='input-group-text' style={{ background: "#d1fae5", borderColor: "#a7f3d0", color: "#059669" }}><i className='bi bi-check2-all' style={{ fontSize: 11 }} /></span>
                          <input id='taskActualPercent' type='number' className='form-control form-control-sm' style={{ borderColor: "#a7f3d0", boxShadow: "none" }} min={0} max={100} value={taskForm.actualPercent} onChange={(e) => setTaskForm((p) => ({ ...p, actualPercent: e.target.value }))} required />
                        </div>
                        <div className='invalid-feedback'>0–100</div>
                      </div>
                    </div>

                    <div className='col-6'>
                      <div style={{ borderLeft: "3px solid #f59e0b", paddingLeft: 10 }}>
                        <label htmlFor='taskPlannedDate' className='form-label mb-1 fw-semibold' style={{ fontSize: "0.84rem", color: "#334155" }}>Planned End</label>
                        <div className='input-group input-group-sm'>
                          <span className='input-group-text' style={{ background: "#fef3c7", borderColor: "#fcd34d", color: "#d97706" }}><i className='bi bi-calendar2-check' style={{ fontSize: 11 }} /></span>
                          <input id='taskPlannedDate' type='date' className='form-control form-control-sm' style={{ borderColor: "#fcd34d", boxShadow: "none" }} value={taskForm.plannedEndDate} onChange={(e) => setTaskForm((p) => ({ ...p, plannedEndDate: e.target.value }))} required />
                        </div>
                        <div className='invalid-feedback'>Required</div>
                      </div>
                    </div>

                    <div className='col-6'>
                      <div style={{ borderLeft: "3px solid #f43f5e", paddingLeft: 10 }}>
                        <label htmlFor='taskActualDate' className='form-label mb-1 fw-semibold' style={{ fontSize: "0.84rem", color: "#334155" }}>Actual End</label>
                        <div className='input-group input-group-sm'>
                          <span className='input-group-text' style={{ background: "#ffe4e6", borderColor: "#fecdd3", color: "#e11d48" }}><i className='bi bi-calendar2-x' style={{ fontSize: 11 }} /></span>
                          <input id='taskActualDate' type='date' className='form-control form-control-sm' style={{ borderColor: "#fecdd3", boxShadow: "none" }} value={taskForm.actualEndDate} onChange={(e) => setTaskForm((p) => ({ ...p, actualEndDate: e.target.value }))} />
                        </div>
                      </div>
                    </div>

                    <div className='col-12'>
                      <div style={{ borderLeft: "3px solid #94a3b8", paddingLeft: 10 }}>
                        <label htmlFor='taskNotes' className='form-label mb-1 fw-semibold' style={{ fontSize: "0.84rem", color: "#334155" }}>
                          Delay / Progress Notes
                          <small className='d-block fw-normal text-muted' style={{ fontSize: "0.71rem" }}>Describe blockers, vendor delays, rework, or recovery steps</small>
                        </label>
                        <textarea id='taskNotes' className='form-control form-control-sm' style={{ borderColor: "#cbd5e1", boxShadow: "none" }} rows={3} title='Progress or delay notes' value={taskForm.notes} onChange={(e) => setTaskForm((p) => ({ ...p, notes: e.target.value }))} minLength={5} required />
                        <div className='invalid-feedback'>Add a short progress note for the task.</div>
                      </div>
                    </div>
                  </div>

                  <div style={{ borderTop: "1.5px dashed #e2e8f0", paddingTop: 14, display: "flex", gap: 8, marginTop: 16 }}>
                    <button type='submit' disabled={!selectedProject || busy} style={{ background: taskForm.id ? "linear-gradient(135deg, #d97706, #f59e0b)" : "linear-gradient(135deg, #0d9488, #14b8a6)", border: "none", color: "#fff", borderRadius: 8, padding: "7px 18px", fontSize: "0.83rem", fontWeight: 700, cursor: busy ? "wait" : "pointer", display: "flex", alignItems: "center", gap: 6, boxShadow: taskForm.id ? "0 3px 10px #d9770633" : "0 3px 10px #0d948833" }}>
                      {busy ? <><span className='spinner-border spinner-border-sm' role='status' aria-hidden='true' /> Saving…</> : <><i className={taskForm.id ? "bi bi-check2-square" : "bi bi-plus-circle-fill"} />{taskForm.id ? "Update Task" : "Add Task"}</>}
                    </button>
                    <button type='button' style={{ background: "#f1f5f9", border: "1.5px solid #cbd5e1", color: "#64748b", borderRadius: 8, padding: "6px 14px", fontSize: "0.83rem", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 5 }} onClick={resetForm} disabled={busy}>
                      <i className='bi bi-arrow-counterclockwise' /> Reset
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div className='col-xl-8'>
            <div style={{ background: "#fff", borderRadius: 14, border: "1.5px solid #e2e8f0", overflow: "hidden", boxShadow: "0 2px 12px #0000000a", marginBottom: 16 }}>
              <div style={{ background: "linear-gradient(135deg, #1e3a5f, #1d4ed8, #3b82f6)", padding: "10px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 30, height: 30, borderRadius: 8, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <i className='bi bi-clipboard2-data-fill' style={{ color: "#fff", fontSize: 14 }} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: "0.9rem", color: "#fff", lineHeight: 1.2 }}>{selectedProject?.title ?? "Project Progress"}</div>
                    <div style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.75)" }}>Module-wise execution progress and delay tracking</div>
                  </div>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  <span style={{ background: "rgba(255,255,255,0.2)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 20, padding: "3px 10px", fontSize: "0.72rem", fontWeight: 600 }}>{summary?.totalTasks ?? 0} Tasks</span>
                  <span style={{ background: "#dbeafe", color: "#1d4ed8", border: "none", borderRadius: 20, padding: "3px 10px", fontSize: "0.72rem", fontWeight: 700 }}>Planned {summary?.averagePlanned ?? 0}%</span>
                  <span style={{ background: "#dcfce7", color: "#15803d", border: "none", borderRadius: 20, padding: "3px 10px", fontSize: "0.72rem", fontWeight: 700 }}>Actual {summary?.averageActual ?? 0}%</span>
                  <span style={{ background: "#fee2e2", color: "#b91c1c", border: "none", borderRadius: 20, padding: "3px 10px", fontSize: "0.72rem", fontWeight: 700 }}>{summary?.delayedTasks ?? 0} Delayed</span>
                </div>
                </div>
              </div>

            {isLoading ? <div className='alert alert-light border'>Loading project progress...</div> : null}

            {!isLoading && selectedProject ? (
              <>
                <div className='table-responsive'>
                  <table className='table align-middle'>
                    <thead className='table-light'>
                      <tr>
                        <th>Module</th>
                        <th>Owner</th>
                        <th>Planned</th>
                        <th>Actual</th>
                        <th>Planned End</th>
                        <th>Health</th>
                        <th>Delay</th>
                        <th className='text-end'>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedProject.progressTasks.map((task) => {
                        const health = getTaskHealth(task);
                        const delayDays = getTaskDelayDays(task);

                        return (
                          <tr key={task.id}>
                            <td>
                              <strong>{task.moduleName}</strong>
                              <div className='small text-muted'>{task.notes}</div>
                            </td>
                            <td>{task.owner}</td>
                            <td>{task.plannedPercent}%</td>
                            <td>{task.actualPercent}%</td>
                            <td>{task.plannedEndDate}</td>
                            <td>
                              <span className={`badge rounded-pill px-3 py-2 ${healthBadgeClass[health]}`}>
                                {health}
                              </span>
                            </td>
                            <td>{delayDays > 0 ? `${delayDays} day(s)` : "On time"}</td>
                            <td className='text-end'>
                              <div className='admin-table-actions justify-content-end'>
                                <button
                                  type='button'
                                  className='btn btn-outline-primary btn-sm admin-icon-btn'
                                  onClick={() => handleEditTask(task)}
                                  aria-label={`Edit ${task.moduleName}`}
                                  title='Edit task'
                                  disabled={busy}
                                >
                                  <i className='bi bi-pencil-square'></i>
                                </button>
                                <button
                                  type='button'
                                  className='btn btn-outline-danger btn-sm admin-icon-btn'
                                  onClick={() => handleDeleteTask(selectedProject, task)}
                                  aria-label={`Delete ${task.moduleName}`}
                                  title='Delete task'
                                  disabled={busy}
                                >
                                  <i className='bi bi-trash'></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {!selectedProject.progressTasks.length ? (
                  <div className='alert alert-light border mt-3 mb-0'>No progress tasks added for this project yet.</div>
                ) : null}
              </>
            ) : null}

            {!isLoading && !selectedProject && !errorMessage ? (
              <div className='alert alert-light border mb-0'>No project records are available yet.</div>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProjectProgressPage;
